import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ValidationModule,
  RowSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  RowStyleModule,
} from "ag-grid-community";
import { SmartPlansListStyled, SkeletonWrapper } from "./styled";
import {
  CustomDropdown,
  CustomTableFooter,
  StatusBadge,
  SnackMsg,
  TableSkeletonLoader,
  ClearFilters,
  NoRowsOverlay,
} from "../../components";
import {
  API_AUTH_TOKEN,
  API_CONTEXT_TYPE,
  API_METHOD_GET,
  COMMON_PAYMENT_CONSTANTS,
  SMART_PLANS_CONSTANTS,
} from "../../config/constants";
import {
  COLUMN_DEFS,
  PAGE_ROUTE,
  SEARCH_CATEGORIES,
  STATUS_FILTER_OPTIONS,
} from "./constants";
import { colors } from "../../config/colors";
import { ExportLogo, SearchLogo } from "assets/icons";
import { Skeleton } from "@mui/material";
import { ADMIN_TYPE, API_URL } from "utils/Constants";
import { ApiService } from "utils/ApiService";

const NoRowsOverlayComponent = () => (
  <NoRowsOverlay
    title={SMART_PLANS_CONSTANTS.NO_DATA_MESSAGE}
    subtitle={SMART_PLANS_CONSTANTS.NO_DATA_SUBTITLE}
  />
);

const SmartPlansList = () => {
  const navigate = useNavigate();

  const [snackMsg, setSnackMsg] = useState("");
  const [showSnack, setShowSnack] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const showFlashMessage = useCallback((msg) => {
    setSnackMsg(msg);
    setShowSnack(true);
  }, []);

  // Read and immediately clear flash message set by cancel flow in details screen
  useEffect(() => {
    const msg = sessionStorage.getItem("flashMsg");
    if (msg) {
      sessionStorage.removeItem("flashMsg");
      showFlashMessage(msg);
    }
  }, [showFlashMessage]);

  const [smartPlans, setSmartPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchCategory, setSearchCategory] = useState(SEARCH_CATEGORIES[0]);
  const [debouncedText, setDebouncedText] = useState("");
  const [debouncedCategory, setDebouncedCategory] = useState(
    SEARCH_CATEGORIES[0],
  );
  // Instant filter — no debounce needed
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);

  const getMerchantId = useCallback(() => {
    const role = localStorage.getItem("role");
    if (role === ADMIN_TYPE.SUPERADMIN)
      return localStorage.getItem("globalMerchantId") || "";
    if (role === ADMIN_TYPE.SCHOOLADMIN)
      return localStorage.getItem("schoolAdminMerchantId") || "";
    return "";
  }, []);

  // Debounce search text only
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText(searchText);
      setDebouncedCategory(searchCategory);
      setPageNo(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, searchCategory]);

  // Builds the search payload from current filter state
  const buildPayload = useCallback(() => {
    const merchantId = getMerchantId();
    const payload = { merchantId, pageNumber: pageNo, pageSize };

    if (debouncedText.trim()) {
      payload[debouncedCategory.value] = debouncedText;
    }
    if (statusFilter.value) {
      payload.status = statusFilter.value;
    }

    return payload;
  }, [pageNo, pageSize, debouncedText, debouncedCategory, statusFilter, getMerchantId]);

  const fetchSmartPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await ApiService.postRequest(
        API_URL.SMART_PLANS,
        payload,
      );
      if (response?.status?.code === "Y") {
        setSmartPlans(response.body?.records ?? []);
        setTotalCount(response.body?.totalItems ?? 0);
      } else {
        setSmartPlans([]);
        setTotalCount(0);
      }
    } catch (err) {
      setSmartPlans([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [buildPayload]);

  useEffect(() => {
    fetchSmartPlans();
  }, [fetchSmartPlans]);

  // ── Filter handlers ──────────────────────────────────────────────────────

  const handleSearchTextChange = useCallback(
    (e) => setSearchText(e.target.value),
    [],
  );

  const handleSearchCategoryChange = useCallback(
    (selectedIndex) => {
      const selected = SEARCH_CATEGORIES[selectedIndex];
      setSearchCategory(selected);
      setSearchText("");
      setDebouncedText("");
      setDebouncedCategory(selected);
    },
    [],
  );

  const handleStatusFilterChange = useCallback((selectedIndex) => {
    setStatusFilter(STATUS_FILTER_OPTIONS[selectedIndex]);
    setPageNo(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter(STATUS_FILTER_OPTIONS[0]);
    setSearchText("");
    setSearchCategory(SEARCH_CATEGORIES[0]);
    setDebouncedText("");
    setDebouncedCategory(SEARCH_CATEGORIES[0]);
    setPageNo(0);
  }, []);

  // ── Pagination handlers ──────────────────────────────────────────────────

  const onPageSizeChanged = useCallback((event) => {
    setPageSize(parseInt(event.target.value));
    setPageNo(0);
  }, []);

  const onLeftArrow = useCallback(() => {
    if (pageNo > 0) setPageNo((p) => p - 1);
  }, [pageNo]);

  const onRightArrow = useCallback(() => {
    if (pageNo < Math.ceil(totalCount / pageSize) - 1) setPageNo((p) => p + 1);
  }, [pageNo, totalCount, pageSize]);

  const columnDefs = useMemo(() => {
    return COLUMN_DEFS.map((col) => {
      if (col.field === "status") {
        return {
          ...col,
          cellRenderer: (params) => <StatusBadge status={params.value} />,
        };
      }
      return col;
    });
  }, []);

  const defaultColDef = useMemo(
    () => ({
      resizable: false,
      sortable: false,
      filter: false,
      suppressMovable: true,
    }),
    [],
  );

  const onRowClicked = useCallback(
    (event) => {
      sessionStorage.setItem("selectedSmartPlan", JSON.stringify(event.data));
      navigate(`${PAGE_ROUTE.SMART_PLANS}/${event.data.id}`);
    },
    [navigate],
  );

  const handleExport = () => {
    setIsExporting(true);
    const authCode = localStorage.getItem("bearer");
    const merchantId = getMerchantId();
    const url = API_URL.EXPORT_SMART_PLANS + merchantId;
    fetch(url, {
      method: API_METHOD_GET,
      headers: {
        Authorization: authCode,
        "X-Auth-Token": API_AUTH_TOKEN,
        Accept: API_CONTEXT_TYPE,
      },
    })
      .then((res) => {
        const auth = res.headers.get("Authorization");
        if (auth) localStorage.setItem("bearer", auth);
        if (!res.ok) throw new Error("Export failed. Please try again later.");
        return res.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `smart_plans_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => {
        const message =
          err?.message || "Export failed. Please try again later.";
        setSnackMsg(message);
        setShowSnack(true);
      })
      .finally(() => setIsExporting(false));
  };

  const renderTableSkeleton = () => (
    <SkeletonWrapper>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={42}
        sx={{ marginBottom: "10px", borderRadius: "4px" }}
      />
      <TableSkeletonLoader rowCount={pageSize} rowHeight={52} />
    </SkeletonWrapper>
  );

  return (
    <SmartPlansListStyled>
      <div className="toolbar">
        <div className="search-filters">
          <CustomDropdown
            label={COMMON_PAYMENT_CONSTANTS.SEARCH_BY}
            value={searchCategory.label}
            items={SEARCH_CATEGORIES.map((cat) => cat.label)}
            height={36}
            width={150}
            onChange={handleSearchCategoryChange}
            disabled={isLoading}
          />
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchTextChange}
              data-testid="search-input"
              disabled={isLoading || isInitialLoading}
              style={{
                cursor: isLoading || isInitialLoading ? "not-allowed" : "text",
                backgroundColor:
                  isLoading || isInitialLoading ? "#f9f9f9" : "white",
              }}
            />
            <SearchLogo className="search-icon" />
          </div>
          <CustomDropdown
            label={COMMON_PAYMENT_CONSTANTS.FILTER_BY_STATUS}
            value={statusFilter.label}
            items={STATUS_FILTER_OPTIONS.map((opt) => opt.label)}
            height={36}
            width={150}
            onChange={handleStatusFilterChange}
            disabled={isLoading}
          />

          {/* Clear Filters Button */}
          <ClearFilters
            hasActiveFilters={statusFilter.value !== "" || searchText}
            onClear={handleClearFilters}
            label={COMMON_PAYMENT_CONSTANTS.CLEAR_FILTERS}
          />
        </div>
        <button
          className="export-button"
          onClick={handleExport}
          data-testid="export-button"
          disabled={totalCount === 0 || isLoading || isExporting}
          style={{
            opacity: totalCount === 0 || isLoading ? 0.6 : 1,
            cursor: totalCount === 0 || isLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isExporting ? (
            <>
              <CircularProgress size={16} color="inherit" />
              Exporting...
            </>
          ) : (
            <>
              <ExportLogo />
              {SMART_PLANS_CONSTANTS.EXPORT}
            </>
          )}
        </button>
      </div>

      {showSnack ? (
        <SnackMsg
          msg={snackMsg}
          show={showSnack}
          onClose={() => setShowSnack(false)}
        />
      ) : null}

      <div className="ag-grid-container">
        <div className="ag-theme-material">
          {isLoading ? (
            renderTableSkeleton()
          ) : (
            <AgGridReact
              rowData={smartPlans}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onRowClicked={onRowClicked}
              rowSelection="single"
              animateRows={true}
              suppressPaginationPanel={true}
              headerHeight={42}
              rowHeight={52}
              rowStyle={{
                borderTop: `1px solid ${colors.storkeLight}`,
                borderBottom: `1px solid ${colors.storkeLight}`,
              }}
              noRowsOverlayComponent={NoRowsOverlayComponent}
              modules={[
                ClientSideRowModelModule,
                ValidationModule,
                RowSelectionModule,
                TextFilterModule,
                NumberFilterModule,
                DateFilterModule,
                CustomFilterModule,
                RowStyleModule,
              ]}
            />
          )}
        </div>
      </div>

      {!isLoading && totalCount > 0 && (
        <CustomTableFooter
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageSizeChanged={onPageSizeChanged}
          onLeftArrow={onLeftArrow}
          onRightArrow={onRightArrow}
        />
      )}
    </SmartPlansListStyled>
  );
};

SmartPlansList.propTypes = {};

export default SmartPlansList;
